
import { faFilePdf, faCloudArrowDown, faEye, faBallotCheck, faFilePlus, faFileCsv, faArrowUpRightFromSquare, faTag, faBoxOpen } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useState, useRef } from 'react'
import LoadingSpinner from '../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import { TwinIconToolTip } from '../../../../../baseComponents/TwinIcon'
import twinFetchPost from '../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import useIsLoading from '../../../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { changeErrorMessage } from '../../../../../utils/reducers/reduxDispatch'
import OrderSelectorPdfTemplateConnect, { CsvOrderSelectorTemplateConnect } from '../../OrderLayout/OrderButtons'
import { PdfTemplateResult } from '../../../../../utils/reducers/company/pdfTemplates'
import useOnClickOutside from '../../../../../utils/hooks/useOnClickOut'
import usePdfTemplateTypes from '../../../../../utils/hooks/pdf/usePdfTemplateTypes'
import ModalPreviewPdfOrder from './ModalPreviewPdfOrder'
import useEditDeleteModal from '../../../../../utils/hooks/useEditDeleteModal'
import ModalCreateWorkSheet from './ModalCreateWorkSheet'
import { useNavigate } from 'react-router'
import locations from '../../../../../utils/globals/locations'
import { OrderData, SetCompleteDataTy } from '../../OrderLayout/types'
import PermissionChecker from '../../../../../baseComponents/PermissionChecker'
import { getUserPermissions } from '../../../../../utils/reducers/getters'
import useCSVTemplateTypes from '../../../../../utils/hooks/csv/useCSVTemplateTypes'
import ModalQuickAccessOrder from './ModalQuickAccessOrder'
import useTagTypes, { TagTypesTy } from '../../../../../utils/hooks/pdf/useTagTypes'
import ModalPrintTagsSell from './ModalPrintTagsSell'
import { ITIconToolTip } from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import ModalPrepareOrder from './ModalPrepareOrder'
import './sellButtons.sass'

interface SellDownloadButtonPDFProps extends SellDownloadButtonPDFLogicProps {
    id: string
    title: string
    className?: string
    children?: React.ReactNode
}
export const SellDownloadButtonPDF: React.FC<SellDownloadButtonPDFProps> = ({ id, title, className, children, ...logic }) => {
    const { downloadPdf, loading } = useSellDownloadButtonPDFLogic({ ...logic })

    if (loading) {
        return <LoadingSpinner className='h-22' />
    }

    return (
        <div onClick={downloadPdf} >
            {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faFilePdf} />}
            <a className='hidden downloadfile' download={true} href='/' target='_blank' onClick={(e) => e.stopPropagation()}>a</a>
        </div>
    )
}

interface SellDownloadButtonPDFLogicProps {
    Order: TwinDictionary
    typeTemplate: keyof PdfTemplateResult
}

const useSellDownloadButtonPDFLogic = ({ typeTemplate, Order }: SellDownloadButtonPDFLogicProps) => {
    const { t } = useTwinTranslation()
    const { endLoading, loading, startLoading } = useIsLoading()
    const errorNotPDFTemplate = t('notPDFTemplate', 'No tienes plantillas de PDF')
    const { pdfTemplateTypes } = usePdfTemplateTypes()

    const downloadPdf = useCallback(async (e: React.MouseEvent) => {
        startLoading()
        const current = e.currentTarget
        const result = await twinFetchPost(pdfTemplateTypes[typeTemplate].url, { id: Order.id })
        if (result) {
            if (result.status === 422) {
                const val = await result.json()
                if (val?.errors[0] && val?.errors[0].msg === 'Not have PDF Template') {
                    changeErrorMessage(errorNotPDFTemplate)
                }
                endLoading()
                return false
            }
            const pdf = await result.blob()
            const fileURL = URL.createObjectURL(pdf)
            const a = current.querySelector('a')
            const fileName = `${Order?.full_assigned_number}-${Order?.Customer?.fiscal_name || Order?.Customer?.name}`
            if (a) {
                a.href = fileURL
                a.download = fileName + '.pdf'
                a.click()
            }
            endLoading()
        }
    }, [Order, errorNotPDFTemplate, pdfTemplateTypes, typeTemplate, startLoading, endLoading])

    return { downloadPdf, loading }
}

interface SellDownloadPdfTemplateButtonProps {
    id: string
    Order: TwinDictionary
    title: string
    className?: string
    typeTemplate: keyof PdfTemplateResult
    children?: React.ReactNode
}

export const SellDownloadPdfTemplateButton: React.FC<SellDownloadPdfTemplateButtonProps> = ({ id, title, className, Order, typeTemplate, children }) => {
    const { showTemplateSelector, setShowTemplateSelector, startLoading, endLoading, loading, ref } = useSellDownloadPdfTemplateButtonLogic()
    return (
        <div className={'flex flex-col relative ' + (className || '')} ref={ref}>
            {loading
                ? <div><LoadingSpinner className='w-24 h-24 m-auto' /></div>
                :
                <div onClick={() => setShowTemplateSelector((old) => !old)}>
                    {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faCloudArrowDown} />}
                </div>
            }
            {showTemplateSelector ? <SellSelectorPdfTemplate Order={Order} handleShowTemplateSelector={() => setShowTemplateSelector((old) => !old)} startLoading={startLoading} endLoading={endLoading} typeTemplate={typeTemplate} /> : null}
        </div>
    )
}


const useSellDownloadPdfTemplateButtonLogic = () => {
    const ref = useRef<any>(null)
    const [showTemplateSelector, setShowTemplateSelector] = useState<boolean>(false)
    const { startLoading, endLoading, loading } = useIsLoading()
    useOnClickOutside(ref, () => setShowTemplateSelector(false))

    return { showTemplateSelector, setShowTemplateSelector, startLoading, endLoading, loading, ref }
}

interface SellSelectorPdfTemplateProps extends SellSelectorPdfTemplateLogicProps { }

const SellSelectorPdfTemplate: React.FC<SellSelectorPdfTemplateProps> = ({ ...rest }) => {
    const { ref, downloadPdfWithTemplate } = useSellSelectorPdfTemplateLogic({ ...rest })
    return (
        <OrderSelectorPdfTemplateConnect downloadPdfWithTemplate={downloadPdfWithTemplate} myref={ref} typeTemplate={rest.typeTemplate} />
    )
}

interface SellSelectorPdfTemplateLogicProps {
    Order: TwinDictionary
    handleShowTemplateSelector: () => void
    startLoading: () => true
    endLoading: () => void
    typeTemplate: keyof PdfTemplateResult
}

const useSellSelectorPdfTemplateLogic = ({ Order, handleShowTemplateSelector, startLoading, endLoading, typeTemplate }: SellSelectorPdfTemplateLogicProps) => {
    const ref = useRef<any>(null)
    const { pdfTemplateTypes } = usePdfTemplateTypes()
    const downloadPdfWithTemplate = useCallback(async (PdfTemplateId: number | string) => {
        startLoading()
        handleShowTemplateSelector()
        const current = ref.current
        const result = await twinFetchPost(pdfTemplateTypes[typeTemplate].url, { id: Order.id, PdfTemplateId })
        if (result) {
            const pdf = await result.blob()
            const fileURL = URL.createObjectURL(pdf)
            const a = current.querySelector('a')
            const fileName = `${Order?.full_assigned_number}-${Order?.Customer?.fiscal_name || Order?.Customer?.name}`
            if (a) {
                a.href = fileURL
                a.download = fileName + '.pdf'
                a.click()
            }
            endLoading()
        }
    }, [handleShowTemplateSelector, Order, endLoading, startLoading, typeTemplate, pdfTemplateTypes])

    return { ref, downloadPdfWithTemplate }
}

interface SellPdfPreviewProps {
    id: string
    Order: TwinDictionary
    title: string
    className?: string
    typeTemplate: keyof PdfTemplateResult
    children?: React.ReactNode
}

export const SellPdfPreview: React.FC<SellPdfPreviewProps> = ({ id, Order, title, className, typeTemplate, children }) => {
    const { showPdfSelector, setShowPdfSelector, openModal, setOpenModal, handlePdfTemplate, ref } = useSellPdfPreviewLogic()
    const { pdfTemplateTypes } = usePdfTemplateTypes()
    return (
        <div className='relative'>
            <div className={'flex flex-col relative ' + (className || '')} onClick={() => setShowPdfSelector((old) => !old)} >
                {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faEye} />}
            </div>
            {showPdfSelector ? <OrderSelectorPdfTemplateConnect typeTemplate={typeTemplate} downloadPdfWithTemplate={handlePdfTemplate} myref={ref} /> : null}
            {openModal?.type === 'edit' ? <ModalPreviewPdfOrder setOpened={() => setOpenModal(null)} onSubmit={() => { }} Order={Order} PdfTemplateId={openModal?.allRowData?.PdfTemplateId} urlGetPdf={pdfTemplateTypes?.[typeTemplate]?.url} /> : null}
        </div>
    )
}

const useSellPdfPreviewLogic = () => {
    const ref = useRef<any>(null)
    const [showPdfSelector, setShowPdfSelector] = useState<boolean>(false)
    const { openModal, setOpenModal } = useEditDeleteModal()
    useOnClickOutside(ref, () => setShowPdfSelector(false))
    const handlePdfTemplate = useCallback((value: any) => {
        if (value) {
            setShowPdfSelector(false)
            setOpenModal({ type: 'edit', allRowData: { PdfTemplateId: value } })
        }
    }, [setOpenModal, setShowPdfSelector])

    return { showPdfSelector, setShowPdfSelector, openModal, setOpenModal, handlePdfTemplate, ref }
}

interface SellCreateWorkSheetButtonProps {
    id: string
    Order: TwinDictionary
    className?: string
    createURL: string
    orderField: 'SellOrderId' | 'SellDeliveryNoteId' | 'SellInvoiceId'
    completeData: OrderData
    children?: React.ReactNode
    title: string
}

export const SellCreateWorkSheetButton: React.FC<SellCreateWorkSheetButtonProps> = ({ id, Order, className, createURL, orderField, completeData, children, title }) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    return (
        <PermissionChecker userPermissions={getUserPermissions('sell.workSheetProductStatus')} permission={'create'}>
            <div className={'flex flex-col relative ' + (className || '')} onClick={() => setOpenModal({ type: 'edit', allRowData: { OrderId: Order.id } })} >
                {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faFilePlus} />}
            </div>
            {openModal?.type === 'edit' ? <ModalCreateWorkSheet OrderId={Order.id} setOpenModal={setOpenModal} orderField={orderField} createURL={createURL} completeData={completeData} /> : null}
        </PermissionChecker>
    )
}

interface SellViewWorkSheetButtonProps {
    id: string
    WorkSheetId: number
    className?: string
    title: string
    children?: React.ReactNode
}

export const SellViewWorkSheetButton: React.FC<SellViewWorkSheetButtonProps> = ({ id, WorkSheetId, className, children, title }) => {
    const navigate = useNavigate()
    return (
        <PermissionChecker userPermissions={getUserPermissions('sell.workSheetProductStatus')} permission={'read'}>
            <div className={'flex flex-col relative ' + (className || '')} onClick={() => navigate(locations.workSheetSingle + '/' + WorkSheetId)}  >
                {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faBallotCheck} />}
            </div>
        </PermissionChecker>
    )
}
interface SellDownloadButtonCSVProps {
    id: string
    Order: TwinDictionary
    title: string
    typeTemplate: keyof PdfTemplateResult
    className?: string
    children?: React.ReactNode
}
export const SellDownloadButtonCSV: React.FC<SellDownloadButtonCSVProps> = ({ id, title, className, Order, typeTemplate, children }) => {
    const { showTemplateSelector, setShowTemplateSelector, startLoading, endLoading, loading, ref } = useSellDownloadCsvTemplateButtonLogic()
    return (
        <div className={'flex flex-col relative ' + (className || '')} ref={ref}>
            {loading
                ? <div><LoadingSpinner className='w-24 h-24 m-auto' /></div>
                :
                <div onClick={() => setShowTemplateSelector((old) => !old)}>
                    {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faFileCsv} />}
                </div>
            }
            {showTemplateSelector ? <SellSelectorCsvTemplate Order={Order} handleShowTemplateSelector={() => setShowTemplateSelector((old) => !old)} startLoading={startLoading} endLoading={endLoading} typeTemplate={typeTemplate} /> : null}
        </div>
    )
}

const useSellDownloadCsvTemplateButtonLogic = () => {
    const ref = useRef<any>(null)
    const [showTemplateSelector, setShowTemplateSelector] = useState<boolean>(false)
    const { startLoading, endLoading, loading } = useIsLoading()
    useOnClickOutside(ref, () => setShowTemplateSelector(false))

    return { showTemplateSelector, setShowTemplateSelector, startLoading, endLoading, loading, ref }
}

interface SellSelectorCsvTemplateProps extends SellSelectorCsvTemplateLogicProps { }

const SellSelectorCsvTemplate: React.FC<SellSelectorCsvTemplateProps> = ({ ...rest }) => {
    const { ref, downloadCsvWithTemplate } = useSellSelectorCsvTemplateLogic({ ...rest })
    return (
        <CsvOrderSelectorTemplateConnect downloadCsvWithTemplate={downloadCsvWithTemplate} myref={ref} />
    )
}

interface SellSelectorCsvTemplateLogicProps {
    Order: TwinDictionary
    handleShowTemplateSelector: () => void
    startLoading: () => true
    endLoading: () => void
    typeTemplate: keyof PdfTemplateResult 
}

const useSellSelectorCsvTemplateLogic = ({ Order, handleShowTemplateSelector, startLoading, endLoading, typeTemplate }: SellSelectorCsvTemplateLogicProps) => {
    const ref = useRef<any>(null)
    const { csvTemplateTypes } = useCSVTemplateTypes()
    const downloadCsvWithTemplate = useCallback(async (CsvOrderTemplateId: number | string) => {
        startLoading()
        handleShowTemplateSelector()
        const current = ref.current
        const result = await twinFetchPost(csvTemplateTypes[typeTemplate].url, { id: Order.id, CsvOrderTemplateId })
        if (result) {
            const csv = await result.blob()
            const fileURL = URL.createObjectURL(csv)
            const a = current.querySelector('a')
            const fileName = `${Order?.full_assigned_number}-${Order?.Customer?.fiscal_name || Order?.Customer?.name}`
            if (a) {
                a.href = fileURL
                a.download = fileName + '.csv'
                a.click()
            }
            endLoading()
        }
    }, [handleShowTemplateSelector, Order, endLoading, startLoading, typeTemplate, csvTemplateTypes])

    return { ref, downloadCsvWithTemplate }
}


interface SellQuickAccessButtonProps {
    Order: TwinDictionary
    className?: string
    title: string
    children?: React.ReactNode
    id: string
}

export const SellQuickAccessButton: React.FC<SellQuickAccessButtonProps> = ({ Order, className, title, children, id }) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    return (
        <div className='relative'>
            <div className={'flex flex-col relative ' + (className || '')} onClick={() => setOpenModal({ type: 'edit', allRowData: { QuoteOrderId: Order?.QuoteOrderId, SellOrderId: Order?.SellOrderId, SellDeliveryNoteId: Order?.SellDeliveryNoteId, SellInvoiceId: Order?.SellInvoiceId, WorkSheetId: Order?.WorkSheetId, relatedSellOrders: Order?.relatedSellOrders, relatedSellDeliveryNotes: Order?.relatedSellDeliveryNotes, relatedSellInvoices: Order?.relatedSellInvoices, relatedWorkSheets: Order?.relatedWorkSheets, relatedOrderRefunds: Order?.relatedOrderRefunds } })} >
                {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faArrowUpRightFromSquare} />}
            </div>
            {openModal?.type === 'edit' ? <ModalQuickAccessOrder allRowData={openModal?.allRowData} setOpenModal={setOpenModal} /> : null}
        </div>
    )
}

interface SellDownloadTagsProps {
    id: string
    Order: TwinDictionary
    title: string
    className?: string
    typeTemplate: TagTypesTy
    children?: React.ReactNode
}

export const SellDownloadTags: React.FC<SellDownloadTagsProps> = ({ id, Order, title, className, typeTemplate, children }) => {
    const { openModal, setOpenModal } = useSellDownloadTagsLogic()
    const { tagTypes } = useTagTypes()

    return (
        <PermissionChecker userPermissions={getUserPermissions('config.label')} permission={'read'} >
            <div className={'flex flex-col relative ' + (className || '')} onClick={() => setOpenModal({ type: 'edit', allRowData: {} })}>
                {children ?? <ITIconToolTip id={id} classNameIcon={'regular24 hover:text-green-43 ' + (className || '')} message={title} icon={faTag} />}
            </div>
            {openModal?.type === 'edit' ? <ModalPrintTagsSell setOpened={() => setOpenModal(null)} onSubmit={() => { }} Order={Order} urlGetTag={tagTypes[typeTemplate].url} /> : null}
        </PermissionChecker>
    )
}

const useSellDownloadTagsLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()

    return { openModal, setOpenModal }
}

interface SellPrepareOrderButtonProps {
    Order: OrderData
    className?: string
    setCompleteData: SetCompleteDataTy
    title: string
    id: string
    children?: React.ReactNode
}
export const SellPrepareOrderButton: React.FC<SellPrepareOrderButtonProps> = ({ className, title, id, children, ...logic }) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    return (
        <div className={'flex flex-col relative ' + (className || '')} >
            <div onClick={() => setOpenModal({ type: 'edit', allRowData: {} })}>
                {children ?? <TwinIconToolTip id={id} classNameIcon={'regular22 hover:text-green-43 ' + (className || '')} message={title} icon={faBoxOpen} />}
            </div>
            {openModal?.type === 'edit' ? <ModalPrepareOrder {...logic} setOpened={() => setOpenModal(null)} /> : null}
        </div>
    )
}